import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill'; // استيراد ReactQuill
import 'react-quill/dist/quill.snow.css'; // استيراد تنسيقات Quill الأساسية (ثيم Snow)
import '../styles/global.css'; // ملف الأنماط العام
import '../styles/articleTemplates.css'; // ملف تنسيق عرض المقال النهائي (للتأكد من تطابق الأعمدة)
import '../styles/customQuillStyles.css'; // <<< ملف CSS الجديد الذي سننشئه
import { validateArticleTitle, validateArticleContent } from '../utils/validation'; // استيراد وظائف التحقق

// تعديل قوالب المحرر لتكون فقط قالبين
const templates = [
  { name: 'Article', requiredColumns: 1, description: 'Regular article displayed in vertical format' },
  { name: 'Art', requiredColumns: 0, description: 'Caricature, photograph, or painting (image only content)' }
];

const CustomEditor = ({ initialContent = '', onSave, user }) => {
  const [content, setContent] = useState(initialContent);
  const [title, setTitle] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('Article');
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({ title: '', content: '', image: '' });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [imageUrl, setImageUrl] = useState(''); // إضافة حالة لرابط الصورة
  const [selectedFile, setSelectedFile] = useState(null); // New state for file upload
  const [uploadOption, setUploadOption] = useState('url'); // 'url' or 'file'
  const [previewUrl, setPreviewUrl] = useState(''); // For image preview

  // تهيئة شريط أدوات Quill - بدون قوائم منسدلة
  const quillModules = {
    toolbar: [
      // [{ 'font': [] }], // يمكن إضافة خطوط إذا أردت
      [{ 'header': '1' }, { 'header': '2' }, { 'header': '3' }, 'blockquote'],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'align': '' }, { 'align': 'center' }, { 'align': 'right' }, { 'align': 'justify' }], // محاذاة LTR
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }], // زيادة/تقليل المسافة البادئة
      // [{ 'color': [] }, { 'background': [] }], // يمكن إضافة ألوان
      ['link', 'image'], // يمكنك إضافة 'video' إذا أردت
      ['clean'] // إزالة التنسيق
    ],
  };

  // التنسيقات المدعومة من Quill (يجب أن تتطابق مع أزرار الشريط)
  const quillFormats = [
    'header', 'blockquote',
    'bold', 'italic', 'underline', 'strike',
    'align',
    'list', 'bullet',
    'indent',
    'link', 'image',
    // 'font', 'color', 'background' // أضف هذه إذا استخدمت الأزرار المقابلة
  ];

  // التحقق من صحة النموذج
  const validateForm = () => {
    const newErrors = { title: '', content: '', image: '' };
    let isValid = true;
    
    // التحقق من العنوان
    if (!title || title.trim() === '') {
      newErrors.title = 'Article title is required';
      isValid = false;
    } else if (title.length < 5) {
      newErrors.title = 'Title must be at least 5 characters';
      isValid = false;
    } else if (title.length > 200) {
      newErrors.title = 'Title must not exceed 200 characters';
      isValid = false;
    }
    
    // التحقق من المحتوى فقط إذا كان المحتوى مقالًا
    if (selectedTemplate === 'Article') {
      if (!content || content.trim() === '') {
        newErrors.content = 'Article content is required';
        isValid = false;
      } else {
        const textContent = content.replace(/<[^>]*>/g, ''); // إزالة علامات HTML
        
        if (textContent.length < 100) {
          newErrors.content = 'Content must be at least 100 characters';
          isValid = false;
        }
      }
    }

    // التحقق من وجود رابط صورة إذا كان القالب المختار هو Art
    if (selectedTemplate === 'Art') {
      if (uploadOption === 'url' && !imageUrl) {
        newErrors.image = 'Image URL is required for Art template';
        isValid = false;
      } else if (uploadOption === 'file' && !selectedFile) {
        newErrors.image = 'Please select an image file to upload';
        isValid = false;
      }
    }
    
    setErrors(newErrors);
    return isValid;
  };

  // Compress image function
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          // Calculate new dimensions (max 800px width or height while maintaining aspect ratio)
          let width = img.width;
          let height = img.height;
          const MAX_SIZE = 800;
          
          if (width > height && width > MAX_SIZE) {
            height *= MAX_SIZE / width;
            width = MAX_SIZE;
          } else if (height > MAX_SIZE) {
            width *= MAX_SIZE / height;
            height = MAX_SIZE;
          }
          
          // Create canvas and draw resized image
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Get compressed data URL with reduced quality (0.6 = 60% quality)
          const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.6);
          resolve(compressedDataUrl);
        };
        img.onerror = error => reject(error);
      };
      reader.onerror = error => reject(error);
    });
  };

  // دالة الحفظ
  const handleSave = async () => {
    setIsSubmitted(true);
    
    // التحقق من صحة النموذج
    if (!validateForm()) {
      return;
    }
    
    // التحقق من وجود معلومات العمود للمستخدم (من Firebase Auth أو غيره)
    if (!user?.column) {
      alert('User column information is missing. Cannot save.');
      console.warn("User object or user.column is missing during save:", user);
      return;
    }

    setIsSaving(true);
    try {
      let finalImageUrl = imageUrl;
      
      // If file upload is selected, handle the file locally
      if (selectedTemplate === 'Art' && uploadOption === 'file' && selectedFile) {
        try {
          // Compress image and convert to data URL
          finalImageUrl = await compressImage(selectedFile);
          console.log("File compressed and converted to data URL successfully");
        } catch (error) {
          console.error('Error processing file:', error);
          alert('Failed to process image. Please try again or use image URL instead.');
          setIsSaving(false);
          return;
        }
      }

      const articleData = {
        title,
        columnTitle: user.column,     // اسم العمود الخاص بالمستخدم
        columnId: user.columnId,      // معرف العمود
        author: user.name,            // اسم المؤلف
        authorUsername: user.username, // اسم مستخدم المؤلف
        content: selectedTemplate === 'Art' ? '' : content, // فارغ للفن، محتوى للمقالات
        template: selectedTemplate,     // معلومات القالب المختار
        createdAt: new Date(),        // تاريخ الإنشاء
        type: selectedTemplate === 'Art' ? 'image' : 'article', // نوع المحتوى (صورة أو مقال)
        isImagePost: selectedTemplate === 'Art', // منشور صورة أم لا
        commentCount: 0,              // عدد التعليقات الأولي
        shareCount: 0,                // عدد المشاركات الأولي
        likes: 0,                     // عدد الإعجابات الأولي
        likedBy: [],                  // قائمة المستخدمين الذين أعجبوا بالمقال
        image: selectedTemplate === 'Art' ? finalImageUrl : null, // رابط الصورة للفن
        imageUrl: finalImageUrl,           // رابط الصورة
        artType: selectedTemplate === 'Art' // إذا كان من نوع فن
      };

      console.log("Saving article data:", articleData);
      await onSave(articleData);
    } catch (error) {
      console.error("Error in handleSave:", error);
      alert('Failed to save article. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // تحديث الأخطاء عند تغيير القيم
  useEffect(() => {
    if (isSubmitted) {
      validateForm();
    }
  }, [title, content, imageUrl, selectedFile, isSubmitted]);

  // Cleanup preview URL on component unmount
  useEffect(() => {
    return () => {
      // Clean up the preview URL to avoid memory leaks
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // دالة لتغيير القالب المختار
  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
  };

  // معالجة تغيير العنوان
  const handleTitleChange = (e) => {
    setTitle(e.target.value);
    if (isSubmitted) {
      const titleValidation = validateArticleTitle(e.target.value);
      setErrors(prev => ({
        ...prev,
        title: titleValidation.valid ? '' : titleValidation.message
      }));
    }
  };

  // معالجة تغيير المحتوى
  const handleContentChange = (value) => {
    setContent(value);
    if (isSubmitted) {
      const contentValidation = validateArticleContent(value);
      setErrors(prev => ({
        ...prev,
        content: contentValidation.valid ? '' : contentValidation.message
      }));
    }
  };

  // معالجة تغيير رابط الصورة
  const handleImageUrlChange = (e) => {
    setImageUrl(e.target.value);
    if (isSubmitted && uploadOption === 'url') {
      validateForm();
    }
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      
      // Create a preview URL for the selected file
      const filePreviewUrl = URL.createObjectURL(file);
      setPreviewUrl(filePreviewUrl);
      
      // Clear any previous errors
      if (isSubmitted) {
        setErrors(prev => ({ ...prev, image: '' }));
      }
    }
  };

  // Handle upload option change
  const handleUploadOptionChange = (option) => {
    setUploadOption(option);
    // Clear errors when switching options
    setErrors(prev => ({ ...prev, image: '' }));
    
    // Reset the other option's value
    if (option === 'url') {
      setSelectedFile(null);
      setPreviewUrl('');
    } else {
      setImageUrl('');
    }
  };

  // الحصول على اسم كلاس CSS للقالب لتطبيقه على حاوية المحرر (للمعاينة)
  const getEditorWrapperClass = () => {
    switch (selectedTemplate) {
      case 'Article':
        return 'editor-wrapper-single';
      case 'Art':
        return 'editor-wrapper-art';
      default:
        return 'editor-wrapper-single';
    }
  };

  return (
    // إضافة كلاس للثيم الكلاسيكي المطلوب
    <div className="editor-container classic-theme">

      {/* --- رأس المحرر: العنوان واختيار القالب --- */}
      <div className="editor-header">
        {/* حقل العنوان */}
        <div className="form-group">
          <label className="settings-label" htmlFor="articleTitle">Article Title</label>
          <input
            id="articleTitle"
            type="text"
            value={title}
            onChange={handleTitleChange}
            className={`settings-input title-input ${errors.title ? 'error' : ''}`}
            placeholder="Enter your article title here..."
          />
          {errors.title && <div className="error-message">{errors.title}</div>}
        </div>

        {/* أزرار اختيار القالب */}
        <div className="editor-templates-selection">
          <label className="settings-label">Select Template:</label>
          <div className="template-buttons-container">
            {templates.map((template, index) => (
              <button
                key={index}
                className={`template-button ${selectedTemplate === template.name ? 'active' : ''}`}
                onClick={() => handleTemplateSelect(template.name)}
                title={template.description}
              >
                {template.name === 'Art' ? 'Art (caricature, photograph, painting)' : template.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* --- Quill Editor or Art Upload --- */}
      <div className={getEditorWrapperClass()}>
        {selectedTemplate === 'Art' ? (
          <div className="art-upload-container">
            {/* Upload option toggles */}
            <div className="upload-options">
              <button 
                className={`option-button ${uploadOption === 'url' ? 'active' : ''}`}
                onClick={() => handleUploadOptionChange('url')}
              >
                Use Image URL
              </button>
              <button 
                className={`option-button ${uploadOption === 'file' ? 'active' : ''}`}
                onClick={() => handleUploadOptionChange('file')}
              >
                Upload Image File
              </button>
            </div>

            {/* Show different input based on selected option */}
            {uploadOption === 'url' ? (
              <div className="form-group">
                <label>Image URL:</label>
                <input
                  type="text"
                  placeholder="Enter image URL..."
                  value={imageUrl}
                  onChange={handleImageUrlChange}
                  className={errors.image ? 'form-control error' : 'form-control'}
                />
              </div>
            ) : (
              <div className="form-group file-upload">
                <label>Upload Image:</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className={errors.image ? 'file-input error' : 'file-input'}
                />
                <div className="file-input-button">
                  <button type="button" onClick={() => document.querySelector('input[type="file"]').click()}>
                    Select Image File
                  </button>
                  <span>{selectedFile ? selectedFile.name : 'No file selected'}</span>
                </div>
              </div>
            )}
            
            {errors.image && <div className="error-message">{errors.image}</div>}
            
            {/* Image preview */}
            {(imageUrl || previewUrl) && (
              <div className="image-preview">
                <h3>Image Preview:</h3>
                <img 
                  src={previewUrl || imageUrl} 
                  alt="Preview" 
                  style={{ maxWidth: '100%', maxHeight: '300px' }} 
                />
                <p className="helper-text">This will appear in the caricature section on the home page.</p>
              </div>
            )}
          </div>
        ) : (
          <ReactQuill
            theme="snow"
            value={content}
            onChange={handleContentChange}
            modules={quillModules}
            formats={quillFormats}
            placeholder="Start writing your masterpiece..."
            style={{ backgroundColor: '#fff' }}
          />
        )}
        {errors.content && selectedTemplate === 'Article' && <div className="error-message" style={{ marginTop: '10px' }}>{errors.content}</div>}
      </div>

      {/* Publish button */}
      <div className="editor-footer">
        <button 
          onClick={handleSave} 
          className="settings-button publish-button" 
          disabled={isSaving}
        >
          {isSaving ? 'Publishing...' : 'Publish Article'}
        </button>
      </div>

    </div>
  );
};

export default CustomEditor;